import React, { useEffect, useState, useRef } from 'react';
import { WEATHER_LEGEND_DATA } from '../home/WeatherMapControls';

/**
 * Surface treatments. `card` boxed the legend as a tile in the row that used to
 * sit between the map and the timeline; `overlay` is the current Global Produce
 * Map treatment — the legend floats ON the map, so the panel is translucent and
 * blurred to keep the tiles underneath readable; `strip` keeps the original
 * top-bordered band used by the home WeatherMap.
 */
const SURFACE_CLASS = {
  card: 'rounded-[8px] border border-[#E8EBE9] bg-white px-[14px] py-[8px] dark:border-[#30363d] dark:bg-[#1d1d1d]',
  overlay:
    'rounded-[10px] border border-white/70 bg-white/70 px-[12px] py-[7px] shadow-[0_2px_10px_rgba(0,0,0,0.12)] backdrop-blur-[6px] dark:border-white/10 dark:bg-[#1d1d1d]/70',
  strip: 'px-4 py-2 border-t border-[#E8EBE9] dark:border-[#3a3c3b]',
};

/**
 * @param {'strip'|'card'|'overlay'} variant see SURFACE_CLASS above.
 */
const WeatherLayerLegend = ({ activeLayer, isDark, variant = 'strip' }) => {
  const [hoverInfo, setHoverInfo] = useState(null);
  const [tooltipLeft, setTooltipLeft] = useState(null);
  const barRef = useRef(null);
  const tooltipRef = useRef(null);
  const isOverlay = variant === 'overlay';
  // Overlay sits on top of colourful weather tiles, so its text runs a shade
  // darker than the muted grey used on the plain white card surface.
  const headingClass = isOverlay
    ? 'text-[11px] leading-[16px] text-[#5D625F] dark:text-[#A8ADA9]'
    : 'text-xs text-[#828382]';
  const legendData = WEATHER_LEGEND_DATA[activeLayer];
  if (!legendData) return null;

  const stops = isDark
    ? (legendData.stopsDark || legendData.stops)
    : (legendData.stopsLight || legendData.stops);

  if (!stops || stops.length < 2) return null;

  const gradientStops = stops.map((stop, index) => {
    const percent = (index / (stops.length - 1)) * 100;
    return `${stop.color} ${percent}%`;
  }).join(', ');

  const getDisplayColor = (color) => {
    if (!color || color === 'transparent') return 'rgba(128,128,128,0.3)';
    if (color.startsWith('rgba') && color.includes(',0)')) return 'rgba(128,128,128,0.3)';
    return color;
  };

  const getDisplayStops = () => {
    if (stops.length <= 7) return stops.map((stop, i) => ({ ...stop, index: i }));
    const maxDisplay = 6;
    const step = (stops.length - 1) / (maxDisplay - 1);
    const indices = [];
    for (let i = 0; i < maxDisplay; i++) indices.push(Math.round(step * i));
    return [...new Set(indices)].map(i => ({ ...stops[i], index: i }));
  };

  const displayStops = getDisplayStops();

  const handleBarInteraction = (clientX) => {
    if (!barRef.current) return;
    const rect = barRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const percent = Math.max(0, Math.min(1, x / rect.width));
    const stopIndex = Math.round(percent * (stops.length - 1));
    setHoverInfo({ x, stop: stops[stopIndex], percent });
  };

  // Clamp tooltip horizontal position so it doesn't overflow the bar container
  // (e.g. near the extreme left/right edge).
  useEffect(() => {
    if (!hoverInfo || !barRef.current || !tooltipRef.current) {
      setTooltipLeft(null);
      return;
    }
    const barRect = barRef.current.getBoundingClientRect();
    const tooltipWidth = tooltipRef.current.offsetWidth || 0;
    if (!barRect.width || tooltipWidth <= 0) {
      setTooltipLeft(null);
      return;
    }
    const half = tooltipWidth / 2;
    const clamped = Math.min(barRect.width - half, Math.max(half, hoverInfo.x));
    setTooltipLeft(clamped);
  }, [hoverInfo, activeLayer, isDark]);

  return (
    // z-[1100] keeps the hover tooltip above Leaflet tile/overlay panes (~200–800)
    // when it extends upward over the map area below the legend bar.
    <div
      className={`relative z-[1100] overflow-visible ${SURFACE_CLASS[variant] || SURFACE_CLASS.strip}`}
    >
      <div className={`flex items-center gap-2 ${isOverlay ? 'mb-1' : 'mb-1.5'}`}>
        <span className={`font-medium ${headingClass}`}>{legendData.label}</span>
        <span className={headingClass}>({legendData.unit})</span>
      </div>

      <div className="relative" style={{ minWidth: isOverlay ? '160px' : '200px' }}>
        <div
          ref={barRef}
          className={`rounded cursor-crosshair touch-none ${isOverlay ? 'h-[10px]' : 'h-3'}`}
          style={{
            background: `linear-gradient(to right, ${gradientStops})`,
            border: isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.1)',
          }}
          onMouseMove={(e) => handleBarInteraction(e.clientX)}
          onMouseLeave={() => setHoverInfo(null)}
          onTouchStart={(e) => e.touches.length > 0 && handleBarInteraction(e.touches[0].clientX)}
          onTouchMove={(e) => { if (e.touches.length > 0) { e.preventDefault(); handleBarInteraction(e.touches[0].clientX); } }}
          onTouchEnd={() => setHoverInfo(null)}
        />

        {hoverInfo && hoverInfo.stop && (
          <div
            className="absolute bottom-full mb-2 z-50 pointer-events-none"
            style={{ left: tooltipLeft ?? hoverInfo.x, transform: 'translateX(-50%)' }}
          >
            <div
              ref={tooltipRef}
              className={`px-2 py-1.5 rounded text-xs whitespace-nowrap shadow-lg ${
              isDark ? 'bg-[#1d1d1d] text-white border border-[#333]' : 'bg-white text-[#282A29] border border-[#e5e7eb]'
              }`}
            >
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: getDisplayColor(hoverInfo.stop.color) }} />
                <div>
                  <div className="font-medium">{hoverInfo.stop.value} {legendData.unit}</div>
                  {hoverInfo.stop.label && <div className="text-[#828382] text-[0.625rem]">{hoverInfo.stop.label}</div>}
                </div>
              </div>
            </div>
            <div
              className="absolute left-1/2 -translate-x-1/2 w-0 h-0"
              style={{
                borderLeft: '6px solid transparent',
                borderRight: '6px solid transparent',
                borderTop: '6px solid',
                borderTopColor: isDark ? '#1d1d1d' : 'white',
              }}
            />
          </div>
        )}

        <div className={`relative mt-0.5 ${isOverlay ? 'h-4' : 'h-5'}`}>
          {displayStops.map((stop) => {
            const percent = (stop.index / (stops.length - 1)) * 100;
            return (
              <div
                key={stop.index}
                className="absolute flex flex-col items-center"
                style={{ left: `${percent}%`, transform: 'translateX(-50%)' }}
              >
                <div className="w-0.5 h-1.5" style={{ backgroundColor: getDisplayColor(stop.color) }} />
                <span className={`text-[0.625rem] tabular-nums whitespace-nowrap ${isOverlay ? 'text-[#5D625F] dark:text-[#A8ADA9]' : 'text-[#828382]'}`}>{stop.value}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default WeatherLayerLegend;
